import { useCallback, useRef, useState } from 'react';
import {
  type Address,
  type AddressDraft,
  type AddressImportRow,
  type AddressReview,
  type City,
  type Country,
} from '@domain/entities';
import { generateAddressImportTemplate } from '@infrastructure/utils/parse-address-import';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAuth } from '../providers/auth-context';
import { useAsync, useDebounced, useSelection } from '../hooks/use-async';
import { useRealtimeVersion } from '../providers/realtime-context';
import { useIsMobile } from '../hooks/use-is-mobile';
import {
  BulkBar,
  Checkbox,
  ConfirmModal,
  EmptyState,
  ErrorState,
  Modal,
  Pager,
  RefreshButton,
  SearchInput,
  StatusBadge,
  TableSkeleton,
  Tag,
  TextField,
} from '../components/ui';
import { PAGE_SIZE } from '../theme/tokens';

const NEW_DRAFT: AddressDraft = {
  name: '',
  category: '',
  city: '',
  address: '',
  phone: '',
  whatsapp: '',
};

function StarRating({ value, count }: { value: number; count: number }) {
  const stars = Math.round(value);
  return (
    <span style={{ fontSize: 13, fontWeight: 700, color: value > 0 ? '#E0A552' : '#B0BBC6', whiteSpace: 'nowrap' }}>
      {'★'.repeat(stars)}{'☆'.repeat(5 - stars)}{' '}
      <span style={{ fontWeight: 600, color: '#4A5560' }}>{value > 0 ? value.toFixed(1) : '—'}</span>
      {count > 0 && (
        <span style={{ fontWeight: 400, color: '#8A95A3', fontSize: 12 }}> ({count})</span>
      )}
    </span>
  );
}

export function AddressesPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const { can } = useAuth();
  const vDirectory = useRealtimeVersion('directory');
  const vDirectoryCategories = useRealtimeVersion('directory-categories');
  const vReviews = useRealtimeVersion('reviews');

  const categoriesResource = useAsync(() => container.directoryCategories.list.execute(), [container, vDirectoryCategories]);
  const countriesResource = useAsync(() => container.settings.listCountries.execute(), [container]);
  const citiesResource = useAsync(() => container.cities.list.execute(), [container]);
  const categoryOptions = categoriesResource.data ?? [];
  const allCountries: Country[] = (countriesResource.data ?? []).filter((c) => c.isActive);
  const allCities: City[] = citiesResource.data ?? [];

  const [tab, setTab] = useState<'list' | 'reviews'>('list');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search);

  const [editorId, setEditorId] = useState<string | null | undefined>(undefined);
  const [draft, setDraft] = useState<AddressDraft>(NEW_DRAFT);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const [reviewsAddress, setReviewsAddress] = useState<Address | null>(null);
  const [addressReviews, setAddressReviews] = useState<AddressReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<AddressImportRow[]>([]);
  const [importFileName, setImportFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [importFilterCountry, setImportFilterCountry] = useState<string>('');
  const [importFilterCategory, setImportFilterCategory] = useState<string>('');
  const fileInput = useRef<HTMLInputElement>(null);

  const selection = useSelection();

  const addresses = useAsync(
    () =>
      container.addresses.search.execute({
        search: debouncedSearch,
        page,
        pageSize: PAGE_SIZE,
      }),
    [container, debouncedSearch, page, vDirectory],
  );

  const flaggedReviews = useAsync(() => container.addresses.listReviews.execute(), [container, vReviews]);

  const isMobile = useIsMobile();
  const rows = addresses.data?.items ?? [];
  const visibleIds = rows.map((row) => row.id);
  const editorOpen = editorId !== undefined;

  const openEditor = useCallback((address: Address | null) => {
    const defaultCountry = allCountries[0]?.code ?? '';
    if (address === null) {
      setEditorId(null);
      setDraft(NEW_DRAFT);
      setSelectedCountryCode(defaultCountry);
      return;
    }
    setEditorId(address.id);
    setDraft({
      name: address.name,
      category: address.category,
      city: address.city,
      address: address.address,
      phone: address.phone ?? '',
      whatsapp: address.whatsapp ?? '',
    });
    const cityRecord = allCities.find((c) => c.name === address.city);
    setSelectedCountryCode(cityRecord?.countryCode ?? defaultCountry);
  }, [allCountries, allCities]);

  const save = useCallback(async () => {
    if (editorId === undefined) return;
    setSaving(true);
    try {
      await container.addresses.save.execute({ id: editorId, draft });
      toast.notify('Adresse enregistrée');
      setEditorId(undefined);
      addresses.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editorId, draft, toast, addresses]);

  const remove = useCallback(
    async (ids: readonly string[]) => {
      try {
        await container.addresses.remove.execute(ids);
        toast.notify(`${ids.length} adresse(s) supprimée(s)`);
        selection.clear();
        addresses.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, selection, addresses],
  );

  const toggleActive = useCallback(
    async (ids: readonly string[], active: boolean) => {
      try {
        await container.addresses.setActive.execute({ ids, active });
        toast.notify(active ? `${ids.length} adresse(s) activée(s)` : `${ids.length} adresse(s) désactivée(s)`);
        selection.clear();
        addresses.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, selection, addresses],
  );

  const openAddressReviews = useCallback(async (address: Address) => {
    setReviewsAddress(address);
    setAddressReviews([]);
    setReviewsLoading(true);
    try {
      const data = await container.addresses.getReviews.execute(address.id);
      setAddressReviews(data);
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setReviewsLoading(false);
    }
  }, [container, toast]);

  const handleDeleteAddressReview = useCallback(async (reviewId: string) => {
    try {
      await container.addresses.deleteAddressReview.execute(reviewId);
      toast.notify('Avis supprimé');
      setAddressReviews((prev) => prev.filter((r) => r.id !== reviewId));
      addresses.reload();
    } catch (error) {
      toast.notifyError(error);
    }
  }, [container, toast, addresses]);

  const previewImport = useCallback(
    async (file: File) => {
      try {
        const parsed = await container.addresses.previewImport.execute(file);
        setImportFileName(file.name);
        setImportRows(parsed);
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast],
  );

  const confirmImport = useCallback(async () => {
    setImporting(true);
    try {
      const created = await container.addresses.confirmImport.execute(importRows);
      toast.notify(`${created.length} adresse(s) importée(s)`);
      setImportOpen(false);
      setImportRows([]);
      setImportFileName('');
      addresses.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setImporting(false);
    }
  }, [container, importRows, toast, addresses]);

  const downloadTemplate = useCallback(() => {
    const blob = generateAddressImportTemplate({
      categories: categoryOptions.map((c) => c.label),
      cities: allCities
        .filter((c) => c.isActive)
        .map((c) => ({
          name: c.name,
          countryName: allCountries.find((co) => co.code === c.countryCode)?.name ?? c.countryCode,
        })),
      countries: allCountries.map((c) => ({ code: c.code, name: c.name })),
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modele-import-adresses.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  }, [categoryOptions, allCities, allCountries]);

  const handleFlaggedReview = useCallback(
    async (id: string, action: 'dismiss' | 'delete') => {
      try {
        if (action === 'dismiss') {
          await container.addresses.dismissReview.execute(id);
          toast.notify('Signalement ignoré');
        } else {
          await container.addresses.deleteReview.execute(id);
          toast.notify('Avis supprimé');
        }
        flaggedReviews.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, flaggedReviews],
  );

  const flaggedCount = flaggedReviews.data?.length ?? 0;

  return (
    <>
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div className="bbm-tabs">
              <button
                type="button"
                className={`bbm-tab${tab === 'list' ? ' bbm-tab--active' : ''}`}
                onClick={() => setTab('list')}
              >
                Adresses
              </button>
              <button
                type="button"
                className={`bbm-tab${tab === 'reviews' ? ' bbm-tab--active' : ''}`}
                onClick={() => setTab('reviews')}
              >
                Avis{flaggedCount > 0 ? ` (${flaggedCount})` : ''}
              </button>
            </div>
            <RefreshButton onRefresh={addresses.reload} loading={addresses.loading} />
          </div>
          {tab === 'list' ? (
            <>
              <SearchInput
                value={search}
                placeholder="Rechercher une adresse…"
                onChange={(next) => { setSearch(next); setPage(1); }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="bbm-btn bbm-btn--ghost" style={{ flex: 1 }} onClick={() => setImportOpen(true)}>
                  Importer Excel
                </button>
                <button type="button" className="bbm-btn bbm-btn--primary" style={{ flex: 1 }} disabled={!can('addresses', 'create')} onClick={() => openEditor(null)}>
                  + Ajouter
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div className="bbm-toolbar">
          <div className="bbm-toolbar__start">
            <div className="bbm-tabs">
              <button
                type="button"
                className={`bbm-tab${tab === 'list' ? ' bbm-tab--active' : ''}`}
                onClick={() => setTab('list')}
              >
                Bonnes adresses
              </button>
              <button
                type="button"
                className={`bbm-tab${tab === 'reviews' ? ' bbm-tab--active' : ''}`}
                onClick={() => setTab('reviews')}
              >
                Avis signalés{flaggedCount > 0 ? ` (${flaggedCount})` : ''}
              </button>
            </div>
            {tab === 'list' ? (
              <SearchInput
                value={search}
                width={220}
                placeholder="Rechercher une adresse…"
                onChange={(next) => { setSearch(next); setPage(1); }}
              />
            ) : null}
          </div>
          <div className="bbm-toolbar__end">
            {tab === 'list' ? (
              <>
                <button type="button" className="bbm-btn bbm-btn--ghost" onClick={() => setImportOpen(true)}>
                  Importer un fichier Excel
                </button>
                <button type="button" className="bbm-btn bbm-btn--primary" disabled={!can('addresses', 'create')} onClick={() => openEditor(null)}>
                  + Ajouter une adresse
                </button>
              </>
            ) : null}
            <RefreshButton onRefresh={addresses.reload} loading={addresses.loading} />
          </div>
        </div>
      )}

      {tab === 'list' ? (
        <>
          {addresses.loading ? <TableSkeleton /> : null}
          {addresses.error ? (
            <ErrorState
              status={addresses.error.status}
              message={addresses.error.message}
              onRetry={addresses.reload}
            />
          ) : null}
          {!addresses.loading && !addresses.error && rows.length === 0 ? (
            <EmptyState title="Aucune adresse" body="Le répertoire est vide pour le moment." />
          ) : null}

          {!addresses.loading && !addresses.error && rows.length > 0 && addresses.data ? (
            <>
              <BulkBar count={selection.selected.length} onClear={selection.clear}>
                <button
                  type="button"
                  className="bbm-bulkbar__ghost"
                  onClick={() => void toggleActive(selection.selected, true)}
                >
                  Activer
                </button>
                <button
                  type="button"
                  className="bbm-bulkbar__ghost"
                  onClick={() => void toggleActive(selection.selected, false)}
                >
                  Désactiver
                </button>
                <button
                  type="button"
                  className="bbm-bulkbar__ghost"
                  disabled={!can('addresses', 'delete')}
                  title={can('addresses', 'delete') ? '' : 'Réservé au super-admin'}
                  onClick={() => setPending({
                    title: 'Supprimer les adresses sélectionnées ?',
                    body: 'Cette action est irréversible.',
                    confirmLabel: 'Supprimer',
                    danger: true,
                    fn: () => void remove(selection.selected),
                  })}
                >
                  Supprimer
                </button>
              </BulkBar>

              {isMobile ? (
                <div className="bbm-card-stack">
                  {rows.map((address) => (
                    <div key={address.id} className="bbm-mobile-card">
                      <div className="bbm-mobile-card__head">
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1, minWidth: 0 }}>
                          <Checkbox
                            checked={selection.isSelected(address.id)}
                            onChange={() => selection.toggle(address.id)}
                            label={`Sélectionner ${address.name}`}
                          />
                          <div style={{ minWidth: 0 }}>
                            <div className="bbm-mobile-card__title">{address.name}</div>
                            <div className="bbm-mobile-card__sub">{address.category}</div>
                          </div>
                        </div>
                        <StatusBadge
                          status={address.publishedAt !== null ? 'approved' : 'inactive'}
                          label={address.publishedAt !== null ? 'Actif' : 'Inactif'}
                        />
                      </div>
                      <div className="bbm-mobile-card__fields">
                        <div className="bbm-mobile-card__field">
                          <span className="bbm-mobile-card__label">Ville</span>
                          <span>{address.city}</span>
                        </div>
                        {address.address ? (
                          <div className="bbm-mobile-card__field">
                            <span className="bbm-mobile-card__label">Adresse</span>
                            <span>{address.address}</span>
                          </div>
                        ) : null}
                      </div>
                      <div className="bbm-mobile-card__actions">
                        <button type="button" className="bbm-btn bbm-btn--ghost" onClick={() => void toggleActive([address.id], address.publishedAt === null)}>
                          {address.publishedAt === null ? 'Activer' : 'Désactiver'}
                        </button>
                        <button type="button" className="bbm-btn" disabled={!can('addresses', 'edit')} onClick={() => openEditor(address)}>Modifier</button>
                        <button type="button" className="bbm-btn bbm-btn--danger" disabled={!can('addresses', 'delete')} title={can('addresses', 'delete') ? '' : 'Réservé au super-admin'} onClick={() => setPending({
                          title: 'Supprimer cette adresse ?',
                          body: 'Cette action est irréversible.',
                          confirmLabel: 'Supprimer',
                          danger: true,
                          fn: () => void remove([address.id]),
                        })}>Supprimer</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bbm-panel">
                  <div className="bbm-row bbm-row--head">
                    <Checkbox
                      checked={selection.allSelected(visibleIds)}
                      onChange={() => selection.toggleAll(visibleIds)}
                      label="Tout sélectionner"
                    />
                    <div style={{ width: 200, flexShrink: 0 }}>Nom</div>
                    <div style={{ width: 120, flexShrink: 0 }}>Catégorie</div>
                    <div style={{ width: 100, flexShrink: 0 }}>Ville</div>
                    <div style={{ width: 120, flexShrink: 0 }}>Téléphone</div>
                    <div style={{ width: 120, flexShrink: 0 }}>WhatsApp</div>
                    <div style={{ width: 60, flexShrink: 0 }}>Statut</div>
                    <div style={{ width: 140, flexShrink: 0 }}>Note</div>
                    <div style={{ marginLeft: 'auto' }}>Actions</div>
                  </div>

                  {rows.map((address) => (
                    <div key={address.id} className="bbm-row">
                      <Checkbox
                        checked={selection.isSelected(address.id)}
                        onChange={() => selection.toggle(address.id)}
                        label={`Sélectionner ${address.name}`}
                      />
                      <div style={{ width: 200, flexShrink: 0 }}>
                        <div className="bbm-cell-strong">{address.name}</div>
                        <div className="bbm-cell-sub bbm-truncate">{address.address}</div>
                      </div>
                      <div style={{ width: 120, flexShrink: 0 }}>
                        <Tag background="#5CA98922">{address.category}</Tag>
                      </div>
                      <div className="bbm-cell" style={{ width: 100, flexShrink: 0 }}>
                        {address.city}
                      </div>
                      <div className="bbm-cell" style={{ width: 120, flexShrink: 0, color: '#8A95A3', fontSize: 12.5 }}>
                        {address.phone ?? '—'}
                      </div>
                      <div className="bbm-cell" style={{ width: 120, flexShrink: 0, color: '#8A95A3', fontSize: 12.5 }}>
                        {address.whatsapp ?? '—'}
                      </div>
                      <div style={{ width: 60, flexShrink: 0 }}>
                        {address.publishedAt !== null ? (
                          <Tag background="#5CA98922" color="#2D6A4F">Actif</Tag>
                        ) : (
                          <Tag background="#F5F6F7" color="#8A95A3">Inactif</Tag>
                        )}
                      </div>
                      <div style={{ width: 140, flexShrink: 0 }}>
                        <button
                          type="button"
                          style={{ background: 'none', border: 'none', cursor: address.reviewsCount > 0 ? 'pointer' : 'default', padding: 0 }}
                          title={address.reviewsCount > 0 ? `Voir les ${address.reviewsCount} avis` : 'Aucun avis'}
                          onClick={() => address.reviewsCount > 0 ? void openAddressReviews(address) : undefined}
                        >
                          <StarRating value={address.rating} count={address.reviewsCount} />
                        </button>
                      </div>
                      <div className="bbm-row__actions">
                        <button
                          type="button"
                          className="bbm-btn bbm-btn--ghost"
                          onClick={() => void toggleActive([address.id], address.publishedAt === null)}
                        >
                          {address.publishedAt === null ? 'Activer' : 'Désactiver'}
                        </button>
                        <button type="button" className="bbm-btn" disabled={!can('addresses', 'edit')} onClick={() => openEditor(address)}>
                          Modifier
                        </button>
                        <button
                          type="button"
                          className="bbm-btn bbm-btn--danger"
                          disabled={!can('addresses', 'delete')}
                          title={can('addresses', 'delete') ? '' : 'Réservé au super-admin'}
                          onClick={() => setPending({
                            title: 'Supprimer cette adresse ?',
                            body: 'Cette action est irréversible.',
                            confirmLabel: 'Supprimer',
                            danger: true,
                            fn: () => void remove([address.id]),
                          })}
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Pager
                page={addresses.data.page}
                pageCount={addresses.data.pageCount}
                total={addresses.data.total}
                pageSize={addresses.data.pageSize}
                onChange={setPage}
              />
            </>
          ) : null}
        </>
      ) : (
        <>
          {flaggedReviews.loading ? <TableSkeleton rows={3} /> : null}
          {flaggedReviews.error ? (
            <ErrorState
              status={flaggedReviews.error.status}
              message={flaggedReviews.error.message}
              onRetry={flaggedReviews.reload}
            />
          ) : null}
          {!flaggedReviews.loading && flaggedCount === 0 ? (
            <EmptyState
              title="Aucun signalement"
              body="Aucun avis n'est actuellement signalé."
            />
          ) : null}
          {(flaggedReviews.data ?? []).map((review) => (
            <div
              key={review.id}
              className="bbm-card"
              style={{ borderRadius: 14, padding: '18px 20px', marginBottom: 12 }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                  gap: 12,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  {review.author}{' '}
                  <span style={{ color: '#8A95A3', fontWeight: 600 }}>→ {review.address}</span>
                </div>
                <Tag background="#E0A55222" color="#8A5B22">
                  {review.reason}
                </Tag>
              </div>
              <p style={{ fontSize: 13.5, color: '#4A5560', lineHeight: 1.5, maxWidth: 640 }}>
                {review.comment}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="bbm-btn"
                  onClick={() => void handleFlaggedReview(review.id, 'dismiss')}
                >
                  Ignorer le signalement
                </button>
                <button
                  type="button"
                  className="bbm-btn bbm-btn--danger"
                  disabled={!can('addresses', 'delete')}
                  title={can('addresses', 'delete') ? '' : 'Réservé au super-admin'}
                  onClick={() => setPending({
                    title: 'Supprimer cet avis ?',
                    body: 'Cette action est irréversible.',
                    confirmLabel: 'Supprimer',
                    danger: true,
                    fn: () => void handleFlaggedReview(review.id, 'delete'),
                  })}
                >
                  Supprimer l'avis
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Modal : éditeur adresse */}
      {editorOpen ? (
        <Modal
          title={editorId === null ? 'Ajouter une adresse' : "Modifier l'adresse"}
          width={660}
          onClose={() => setEditorId(undefined)}
          onSubmit={() => void save()}
          busy={saving}
        >
          <TextField
            label="Nom de l'adresse"
            value={draft.name}
            onChange={(value) => setDraft({ ...draft, name: value })}
          />
          <div style={{ marginBottom: 12 }}>
            <label className="bbm-label">Catégorie</label>
            <select
              className="bbm-input"
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            >
              {categoryOptions.map((c) => (
                <option key={c.id} value={c.label}>{c.label}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label className="bbm-label">Pays</label>
              <select
                className="bbm-input"
                value={selectedCountryCode}
                onChange={(e) => {
                  setSelectedCountryCode(e.target.value);
                  setDraft((d) => ({ ...d, city: '' }));
                }}
              >
                {allCountries.map((c) => (
                  <option key={c.id} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="bbm-label">Ville</label>
              <select
                className="bbm-input"
                value={draft.city}
                onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
              >
                <option value="">— choisir —</option>
                {allCities
                  .filter((c) => !selectedCountryCode || c.countryCode === selectedCountryCode)
                  .filter((c) => c.isActive)
                  .map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
              </select>
            </div>
          </div>
          <TextField
            label="Adresse complète"
            value={draft.address}
            onChange={(value) => setDraft({ ...draft, address: value })}
          />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <TextField
              label="Téléphone"
              value={draft.phone ?? ''}
              onChange={(value) => setDraft({ ...draft, phone: value })}
              placeholder="+225 07 00 00 00 00"
            />
            <TextField
              label="WhatsApp"
              value={draft.whatsapp ?? ''}
              onChange={(value) => setDraft({ ...draft, whatsapp: value })}
              placeholder="+225 07 00 00 00 00"
            />
          </div>
        </Modal>
      ) : null}

      {/* Modal : avis de l'adresse */}
      {reviewsAddress !== null ? (
        <Modal
          title={`Avis — ${reviewsAddress.name}`}
          width={520}
          onClose={() => setReviewsAddress(null)}
        >
          {reviewsLoading ? (
            <TableSkeleton rows={3} />
          ) : addressReviews.length === 0 ? (
            <EmptyState title="Aucun avis" body="Cette adresse n'a pas encore reçu d'avis." />
          ) : (
            addressReviews.map((review) => (
              <div
                key={review.id}
                style={{
                  padding: '14px 0',
                  borderBottom: '1px solid #E4E8ED',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 13.5 }}>{review.author}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: '#E0A552', fontWeight: 700, fontSize: 13 }}>
                      {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)} {review.rating}/5
                    </span>
                    <span style={{ fontSize: 11, color: '#8A95A3' }}>{review.createdAt}</span>
                    {can('addresses', 'delete') && (
                      <button
                        type="button"
                        className="bbm-btn bbm-btn--danger"
                        style={{ fontSize: 11, padding: '2px 8px' }}
                        onClick={() => setPending({
                          title: 'Supprimer cet avis ?',
                          body: 'Cette action est irréversible.',
                          confirmLabel: 'Supprimer',
                          danger: true,
                          fn: () => void handleDeleteAddressReview(review.id),
                        })}
                      >
                        Supprimer
                      </button>
                    )}
                  </div>
                </div>
                {review.comment ? (
                  <p style={{ margin: 0, fontSize: 13, color: '#4A5560', lineHeight: 1.5 }}>
                    {review.comment}
                  </p>
                ) : null}
              </div>
            ))
          )}
        </Modal>
      ) : null}

      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {/* Modal : import Excel */}
      {importOpen ? (
        <Modal
          title="Importer des adresses (Excel)"
          width={620}
          onClose={() => {
            setImportOpen(false);
            setImportRows([]);
            setImportFilterCountry('');
            setImportFilterCategory('');
          }}
          onSubmit={importRows.length > 0 ? () => void confirmImport() : undefined}
          submitLabel={`Importer ${importRows.length} adresse(s)`}
          busy={importing}
        >
          {/* Bloc modèle */}
          <div
            style={{
              background: '#F0F6FF',
              border: '1px solid #C8DEFF',
              borderRadius: 12,
              padding: '14px 16px',
              marginBottom: 16,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#2B5FAB', marginBottom: 10 }}>
              Modèle Excel — valeurs attendues
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div style={{ minWidth: 0 }}>
                <label className="bbm-label" style={{ color: '#2B5FAB' }}>Catégorie</label>
                <select
                  className="bbm-input"
                  style={{ width: '100%', minWidth: 0 }}
                  value={importFilterCategory}
                  onChange={(e) => setImportFilterCategory(e.target.value)}
                >
                  <option value="">Toutes</option>
                  {categoryOptions.map((c) => (
                    <option key={c.id} value={c.label}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ minWidth: 0 }}>
                <label className="bbm-label" style={{ color: '#2B5FAB' }}>Pays</label>
                <select
                  className="bbm-input"
                  style={{ width: '100%', minWidth: 0 }}
                  value={importFilterCountry}
                  onChange={(e) => setImportFilterCountry(e.target.value)}
                >
                  <option value="">Tous</option>
                  {allCountries.map((c) => (
                    <option key={c.id} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ minWidth: 0 }}>
                <label className="bbm-label" style={{ color: '#2B5FAB' }}>Ville</label>
                <select className="bbm-input" style={{ width: '100%', minWidth: 0 }} disabled>
                  <option>
                    {allCities.filter((c) => c.isActive && (!importFilterCountry || c.countryCode === importFilterCountry)).length} ville(s) dispo.
                  </option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                className="bbm-btn"
                style={{ fontSize: 13, flexShrink: 0 }}
                onClick={downloadTemplate}
              >
                ⬇ Télécharger le modèle .xlsx
              </button>
              <span style={{ fontSize: 12, color: '#6B82A8', minWidth: 0 }}>
                Contient les catégories et villes valides en feuille de référence
              </span>
            </div>
          </div>

          <p style={{ fontSize: 13, color: '#8A95A3', marginTop: 0, marginBottom: 12 }}>
            Colonnes attendues : Nom, Catégorie, Pays, Ville, Adresse, Téléphone, WhatsApp. Aucune
            écriture n'est effectuée avant confirmation.
          </p>

          {importRows.length === 0 ? (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                border: '2px dashed #DDE2E7',
                borderRadius: 14,
                padding: '40px 20px',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 28 }} aria-hidden="true">
                📄
              </span>
              <span style={{ fontSize: 13.5, color: '#33506B', fontWeight: 700 }}>
                Cliquer pour parcourir un fichier .xlsx ou .csv
              </span>
              <span style={{ fontSize: 12, color: '#8A95A3' }}>
                Extraction automatique des lignes
              </span>
            </button>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: '#33506B', fontWeight: 700 }}>
                  {importFileName} — {importRows.length} lignes détectées
                </span>
                <button
                  type="button"
                  className="bbm-btn bbm-btn--ghost"
                  style={{ fontSize: 12 }}
                  onClick={() => {
                    setImportRows([]);
                    setImportFileName('');
                    fileInput.current?.click();
                  }}
                >
                  Changer de fichier
                </button>
              </div>
              <div
                style={{
                  maxHeight: 280,
                  overflow: 'auto',
                  border: '1px solid #E4E8ED',
                  borderRadius: 10,
                  marginBottom: 18,
                }}
              >
                <div className="bbm-row bbm-row--head" style={{ padding: '9px 14px', gap: 12 }}>
                  <div style={{ width: 140, flexShrink: 0 }}>Nom</div>
                  <div style={{ width: 100, flexShrink: 0 }}>Catégorie</div>
                  <div style={{ width: 80, flexShrink: 0 }}>Ville</div>
                  <div style={{ width: 130, flexShrink: 0 }}>Adresse</div>
                  <div style={{ width: 110, flexShrink: 0 }}>Téléphone</div>
                  <div style={{ width: 110, flexShrink: 0 }}>WhatsApp</div>
                </div>
                {importRows.map((row) => (
                  <div
                    key={`${row.name}-${row.phone}`}
                    className="bbm-row"
                    style={{ padding: '9px 14px', gap: 12, fontSize: 13 }}
                  >
                    <div className="bbm-cell-strong" style={{ width: 140, flexShrink: 0 }}>
                      {row.name}
                    </div>
                    <div style={{ width: 100, flexShrink: 0 }}>{row.category}</div>
                    <div style={{ width: 80, flexShrink: 0 }}>{row.city}</div>
                    <div style={{ width: 130, flexShrink: 0, color: '#8A95A3' }}>{row.address ?? '—'}</div>
                    <div style={{ width: 110, flexShrink: 0 }}>{row.phone ?? '—'}</div>
                    <div style={{ width: 110, flexShrink: 0 }}>{row.whatsapp ?? '—'}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void previewImport(file);
              event.target.value = '';
            }}
          />
        </Modal>
      ) : null}
    </>
  );
}
